import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  User as UserIcon,
  Check,
  Loader2,
  Plus,
  X
} from 'lucide-react';

/**
 * UserSearchSelect Component
 * Async user search with debouncing and dropdown selection
 * 
 * Props:
 * - value: Selected user ID
 * - onChange: Callback when user selected (userId, userObject)
 * - onCreateNew: Optional callback for "Create new user"
 * - placeholder: Placeholder text
 * - error: Error message to display
 * - disabled: Disable input
 * - required: Mark as required field
 * - className: Additional CSS classes
 * - searchApi: Optional async function to search users
 * - users: Static list of users (alternative to searchApi)
 */
const UserSearchSelect = ({
  value,
  onChange,
  onCreateNew,
  placeholder = 'Search users by name or email...',
  error,
  disabled = false,
  required = false,
  className = '',
  searchApi,
  users = []
}) => {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Refs
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Get display value
  useEffect(() => {
    if (value && !selectedUser) {
      // Find selected user from results or users list
      const user = searchResults.find(u => u.id === value) || 
                   users.find(u => u.id === value);
      if (user) {
        setSelectedUser(user);
        setSearchQuery(user.name || user.email);
      }
    }
  }, [value, searchResults, users, selectedUser]);

  // Debounced search
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't search if query is too short or input not focused
    if (!isOpen || searchQuery.length < 1) {
      setSearchResults([]);
      return;
    }

    // Set loading state
    setIsLoading(true);

    // Debounce search by 300ms
    debounceTimerRef.current = setTimeout(async () => {
      try {
        if (searchApi) {
          // Use async API search
          const results = await searchApi(searchQuery);
          setSearchResults(results || []);
        } else {
          // Filter static users list
          const filtered = users.filter(user =>
            user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.role?.toLowerCase().includes(searchQuery.toLowerCase())
          );
          setSearchResults(filtered);
        }
      } catch (error) {
        console.error('User search error:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, isOpen, searchApi, users]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle user selection
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery(user.name || user.email);
    setIsOpen(false);
    
    if (onChange) {
      onChange(user.id, user);
    }
  };

  // Handle clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedUser(null);
    setSearchQuery('');
    setSearchResults([]);
    
    if (onChange) {
      onChange('', null);
    }
    
    // Focus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true);
    
    // Show all users on focus if no search query
    if (!searchQuery && !searchApi) {
      setSearchResults(users);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear selection if query changed
    if (selectedUser) {
      setSelectedUser(null);
      if (onChange) {
        onChange('', null);
      }
    }
    
    // Open dropdown
    setIsOpen(true);
  };

  // Handle create new user
  const handleCreateNew = () => {
    setIsOpen(false);
    if (onCreateNew) {
      onCreateNew(searchQuery);
    }
  };

  // Get user avatar initial
  const getUserInitial = (user) => {
    if (user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return '?';
  };

  return (
    <div className={`relative ${className}`}>
      {/* Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100 transition-colors ${
            error
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-300 dark:border-gray-700'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        
        {/* Clear button */}
        {searchQuery && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            tabIndex={-1}
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto"
        >
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400 animate-spin" />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                Searching...
              </span>
            </div>
          )}

          {/* No results */}
          {!isLoading && searchQuery && searchResults.length === 0 && (
            <div className="py-8 text-center">
              <UserIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                No users found matching "{searchQuery}"
              </p>
              
              {/* Create new user option */}
              {onCreateNew && (
                <button
                  type="button"
                  onClick={handleCreateNew}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create new user
                </button>
              )}
            </div>
          )}

          {/* Search results */}
          {!isLoading && searchResults.length > 0 && (
            <>
              {/* Create new option at top */}
              {onCreateNew && searchQuery && (
                <>
                  <button
                    type="button"
                    onClick={handleCreateNew}
                    className="w-full px-4 py-3 text-left hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors border-b border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
                        <Plus className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                          Create new user
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {searchQuery}
                        </p>
                      </div>
                    </div>
                  </button>
                </>
              )}

              {/* User results */}
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleSelectUser(user)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.name || user.email}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
                          <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                            {getUserInitial(user)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* User info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {user.name || 'Unknown'}
                        </p>
                        {user.role && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
                            {user.role}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>

                    {/* Selected indicator */}
                    {selectedUser?.id === user.id && (
                      <div className="flex-shrink-0">
                        <Check className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}

          {/* Empty state (no query yet) */}
          {!isLoading && !searchQuery && searchResults.length === 0 && (
            <div className="py-8 text-center">
              <Search className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Start typing to search users
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearchSelect;
